import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, Input, Empty, Typography, Grid, Breadcrumb, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import HeroSlider from '../components/HeroSlider';
import ProductImage from '../components/ProductImage';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

const BRAND = 'var(--brand-primary, #222957)';

// 視覺隱藏但爬蟲/螢幕閱讀器讀得到（給頁面一個明確 H1，不影響版面）。
const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
};

// 商品卡片（桌機/手機共用；hover 抬升效果）
function ProductCard({ p, onClick }) {
  return (
    <Card
      hoverable
      onClick={onClick}
      className="shop-product-card"
      style={{ cursor: 'pointer', overflow: 'hidden', borderRadius: 14, transition: 'transform .18s ease, box-shadow .18s ease' }}
      styles={{ body: { padding: 12 } }}
      cover={<ProductImage src={p.image_url} alt={p.name} />}
    >
      <Text strong ellipsis style={{ display: 'block', fontSize: 14 }}>{p.name}</Text>
      <Text style={{ color: BRAND, fontWeight: 700, fontSize: 16 }}>
        {p.price != null ? `NT$ ${p.price}` : '—'}
      </Text>
    </Card>
  );
}

export default function ShopHome() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const isShopPage = location.pathname === '/shop';

  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';

  const [keyword, setKeyword] = useState(initialQ);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null); // 商店設定（hero/promo/featured/title）
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null); // null=全部
  const [sort, setSort] = useState('store_recommended');
  const [crystalFilter, setCrystalFilter] = useState([]); // 水晶多選
  const [stoneSize, setStoneSize] = useState(null);        // 主石大小
  const [crystalOptions, setCrystalOptions] = useState([]);
  const [stoneSizeOptions, setStoneSizeOptions] = useState([]);
  const debounceRef = useRef(null);

  // 抓商店首頁設定（hero/promo/featured）— 缺 API/空資料時優雅退場
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/settings/shop`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (alive) setShop(data && typeof data === 'object' ? data : null); })
      .catch(() => { if (alive) setShop(null); });
    return () => { alive = false; };
  }, []);

  // 依 q / 功效 / 排序 / 水晶 / 主石大小 抓商品
  const fetchProducts = useCallback((q, cat, srt, crys, size) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (cat) params.set('effect', cat);
    if (srt) params.set('sort', srt);
    if (Array.isArray(crys) && crys.length) params.set('crystals', crys.join(','));
    if (size) params.set('stone_size', size);
    fetch(`${API_BASE}/api/catalog/public/products?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  // 篩選任一改變時抓資料
  useEffect(() => {
    fetchProducts(searchParams.get('q') || '', category, sort, crystalFilter, stoneSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, category, sort, crystalFilter, stoneSize]);

  // 篩選選項清單（功效 / 水晶 / 主石大小）
  useEffect(() => {
    let alive = true;
    const load = (path, setter) => fetch(`${API_BASE}${path}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (alive) setter(Array.isArray(d) ? d : []); })
      .catch(() => {});
    load('/api/catalog/public/effect-categories', setCategories);
    load('/api/catalog/public/crystals', setCrystalOptions);
    load('/api/catalog/public/stone-sizes', setStoneSizeOptions);
    return () => { alive = false; };
  }, []);

  // 輸入時 debounce 更新網址 ?q=
  const onSearchChange = useCallback((e) => {
    const v = e.target.value;
    setKeyword(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = new URLSearchParams();
      if (v.trim()) next.set('q', v.trim());
      setSearchParams(next, { replace: true });
    }, 400);
  }, [setSearchParams]);

  // ---- 由設定衍生的資料（全部有 fallback）----
  const heroSlides = Array.isArray(shop?.hero_slides) ? shop.hero_slides.filter((s) => s && s.image) : [];
  const featuredIds = Array.isArray(shop?.featured_product_ids) ? shop.featured_product_ids : [];

  const isSearching = !!(searchParams.get('q') || '').trim();

  // 首頁自訂商品區塊（後台可拖曳排序）；沒設定時退回舊的「精選商品」當一塊，維持相容
  const rawSections = Array.isArray(shop?.sections) ? shop.sections : [];
  const effectiveSections = rawSections.length
    ? rawSections
    : (featuredIds.length ? [{ title: '精選商品', product_ids: featuredIds }] : []);
  // 只在預設瀏覽視圖（沒搜尋、沒選分類）顯示自訂區塊；「所有商品」永遠固定最下面
  const defaultView = !isSearching && !category && crystalFilter.length === 0 && !stoneSize;

  // slider 下方麵包屑
  const breadcrumbItems = [{ title: '首頁' }];
  if (isSearching) breadcrumbItems.push({ title: `搜尋：${(searchParams.get('q') || '').trim()}` });
  else if (category) breadcrumbItems.push({ title: category });

  const goProduct = (id) => navigate(`/product/${id}`);

  // ---- 版面：桌機 full-bleed hero + 置中內容(1240) + 自動填滿商品格；手機緊湊堆疊 ----
  const root = { width: '100%', minHeight: '100%', background: isMobile ? '#fff' : '#F9FAFD' };
  const contentWrap = { maxWidth: isMobile ? '100%' : 1240, margin: '0 auto', padding: isMobile ? '0 14px 28px' : '0 32px 60px' };
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: isMobile ? 12 : 22,
  };
  // 商品區塊：與「所有商品」一致，電腦版一排五個（auto-fill 210px）
  const sectionGridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: isMobile ? 12 : 22,
  };

  const SectionHeading = ({ children, extra }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, margin: isMobile ? '20px 0 12px' : '36px 0 18px' }}>
      <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: BRAND, fontWeight: 800, letterSpacing: 1, textAlign: 'center' }}>
        {children}
      </Title>
      {extra}
    </div>
  );

  const searchBar = (
    <Input
      size="large"
      allowClear
      prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
      placeholder="搜尋商品名稱"
      value={keyword}
      onChange={onSearchChange}
      style={{ borderRadius: 12, ...(isMobile ? { fontSize: 16, height: 46 } : {}) }}
    />
  );

  // Hero：有設定用輪播；沒設定用品牌漸層 banner（桌機/手機都有存在感、不會空）
  const renderHero = () => {
    // 商店設定（含輪播）還沒載入好時：先放中性佔位，避免載入前閃出紫色底色塊。
    // 用 shop===null 判斷（設定與商品是各自 fetch，不能只看 products 的 loading）。
    if (shop === null) {
      return <div style={{ width: '100%', height: isMobile ? 220 : 340, borderRadius: isMobile ? 0 : 16, background: '#EDEFF8' }} />;
    }
    if (heroSlides.length > 0) return <HeroSlider slides={heroSlides} />;
    // 沒設定輪播時不顯示任何 hero（避免進站看到預設紫色大色塊）
    return null;
  };

  return (
    <div style={root}>
      <h1 style={SR_ONLY}>
        {isShopPage
          ? '水晶手鍊商城 — 天然石與客製能量手鍊｜星河耀眼 ShiningStar Jewelry'
          : '星河耀眼 ShiningStar Jewelry — 客製水晶手鍊・天然石飾品品牌'}
      </h1>
      <style>{`.shop-product-card:hover { transform: translateY(-4px); box-shadow: 0 10px 24px rgba(34,41,87,0.16); }`}</style>

      {/* Hero：滿版（電腦版用 centerMode，中間張＝內容寬、左右露出淡出） */}
      {renderHero()}

      <div style={contentWrap}>
        {/* slider 下方麵包屑：一般首頁不顯示（只有「首頁」二字沒意義），搜尋/篩選時才顯示情境 */}
        {(isSearching || category) && (
          <Breadcrumb style={{ margin: isMobile ? '14px 0 2px' : '20px 0 4px' }} items={breadcrumbItems} />
        )}

        {/* 搜尋已移到 header（手機版點放大鏡開對話框）；篩選移到「所有商品」標題下方 */}

        {/* 自訂區塊（後台可拖曳排序）：商品區塊 / 圖文區塊；只在預設瀏覽視圖顯示 */}
        {defaultView && effectiveSections.map((sec, si) => {
          // 圖文區塊：一半圖一半文，可選圖左／圖右
          if (sec.type === 'image_text') {
            const rawText = sec.text || '';
            const isHtml = /<\w+[^>]*>/.test(rawText); // WYSIWYG 產生的 HTML
            const paras = rawText.split(/\n{2,}|\n/).map((s) => s.trim()).filter(Boolean);
            if (!sec.image && !rawText.trim()) return null;
            const imgRight = sec.image_side === 'right';
            // 圖:文 比例（image 佔比）：50 / 66 / 55；電腦版用 flex-grow 呈現
            const RATIO = { 50: [50, 50], 66: [66, 33], 55: [55, 45] };
            const [imgFlex, txtFlex] = RATIO[Number(sec.ratio)] || [50, 50];
            return (
              <React.Fragment key={`sec-${si}`}>
                {sec.title ? <SectionHeading>{sec.title}</SectionHeading> : <div style={{ height: isMobile ? 16 : 28 }} />}
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : (imgRight ? 'row-reverse' : 'row'),
                  gap: isMobile ? 14 : 32,
                  alignItems: 'stretch',
                  minHeight: isMobile ? 'auto' : 300,
                  marginBottom: isMobile ? 24 : 36,
                }}>
                  {sec.image ? (
                    <div style={{ flex: isMobile ? '1 1 auto' : `${imgFlex} 1 0`, minWidth: 0 }}>
                      <img
                        src={sec.image}
                        alt={sec.title || ''}
                        style={{ width: '100%', height: isMobile ? 200 : '100%', objectFit: 'cover', display: 'block', borderRadius: 16 }}
                      />
                    </div>
                  ) : null}
                  <div style={{ flex: isMobile ? '1 1 auto' : `${txtFlex} 1 0`, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: isMobile ? '2px 2px' : '8px 4px' }}>
                    {isHtml ? (
                      <div
                        className="image-text-html"
                        style={{ fontSize: isMobile ? 15 : 16, lineHeight: 1.95, color: '#2C3674', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                        dangerouslySetInnerHTML={{ __html: rawText }}
                      />
                    ) : (
                      paras.map((p, k) => (
                        <p key={k} style={{ fontSize: isMobile ? 15 : 16, lineHeight: 1.95, color: '#2C3674', margin: '0 0 12px', overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{p}</p>
                      ))
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          }
          // 商品區塊
          const secProducts = (sec.product_ids || [])
            .map((pid) => products.find((p) => p.odoo_product_id === pid))
            .filter(Boolean);
          if (secProducts.length === 0) return null;
          return (
            <React.Fragment key={`sec-${si}`}>
              <SectionHeading>{sec.title || '精選'}</SectionHeading>
              <div style={sectionGridStyle}>
                {secProducts.map((p) => (
                  <ProductCard key={`sec-${si}-${p.odoo_product_id}`} p={p} onClick={() => goProduct(p.odoo_product_id)} />
                ))}
              </div>
            </React.Fragment>
          );
        })}

        {/* 所有商品（永遠固定在最下面） */}
        <SectionHeading>{isSearching ? '搜尋結果' : '所有商品'}</SectionHeading>

        {/* 篩選：只影響「所有商品」清單，點下去下拉選 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap', margin: '0 0 18px' }}>
          <Select
            value={category || undefined}
            onChange={(v) => setCategory(v || null)}
            allowClear
            placeholder="功效（全部）"
            style={{ width: 150 }}
            options={categories.map((c) => ({ value: c, label: c }))}
          />
          {crystalOptions.length > 0 && (
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              value={crystalFilter}
              onChange={setCrystalFilter}
              placeholder="水晶（可多選）"
              style={{ minWidth: 180, maxWidth: 320 }}
              optionFilterProp="label"
              options={crystalOptions.map((c) => ({ value: c, label: c }))}
            />
          )}
          {stoneSizeOptions.length > 0 && (
            <Select
              value={stoneSize || undefined}
              onChange={(v) => setStoneSize(v || null)}
              allowClear
              placeholder="主石大小"
              style={{ width: 130 }}
              options={stoneSizeOptions.map((s) => ({ value: s, label: s }))}
            />
          )}
          <Select
            value={sort}
            onChange={setSort}
            style={{ width: 140 }}
            options={[
              { value: 'store_recommended', label: '店家推薦' },
              { value: 'newest', label: '最新上架' },
              { value: 'oldest', label: '最舊上架' },
              { value: 'price_asc', label: '價格低→高' },
              { value: 'price_desc', label: '價格高→低' },
              { value: 'popular', label: '熱門度' },
            ]}
          />
        </div>

        {loading ? (
          // 載入中：先擺基本佔位卡（版面穩定、不閃），載完再換成實際商品
          <div style={gridStyle}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`sk-${i}`} style={{ borderRadius: 14, overflow: 'hidden', background: '#fff', border: '1px solid #EBEDF7' }}>
                <div style={{ width: '100%', aspectRatio: '1 / 1', background: '#EDEFF8' }} />
                <div style={{ padding: 12 }}>
                  <div style={{ height: 12, width: '80%', background: '#E5E7F4', borderRadius: 6, marginBottom: 8 }} />
                  <div style={{ height: 14, width: '45%', background: '#E5E7F4', borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <Empty description="目前沒有符合的商品" style={{ padding: '60px 0' }} />
        ) : (
          <div style={gridStyle}>
            {products.map((p) => (
              <ProductCard key={p.odoo_product_id} p={p} onClick={() => goProduct(p.odoo_product_id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
